import { useEffect, useRef } from 'react';

/**
 * Lets the system Back button close an in-page layer instead of leaving the
 * screen underneath it.
 *
 * The Android shell sends Back to `WebView.goBack()`, which walks the SPA's
 * history (MainActivity). A full-screen layer that has no history entry of its
 * own - the phone cart sheet on the POS - would therefore be skipped: Back
 * would go to the previous *page* and take the open ticket's screen with it.
 * So while the layer is open it owns one entry, and Back pops that entry.
 *
 * The entry keeps the router's own state (`idx`, `key`), so React Router sees a
 * pop to a location it is already on and does nothing. When the layer is
 * closed by anything other than Back - a button, a settled bill - the entry is
 * taken off again, so the history does not grow a dead step every time.
 */
export function useBackToClose(open: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    // A string, not an object: history.state is structured-cloned, so an object
    // put in would never be identical to the one read back.
    const marker = `layer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.history.pushState({ ...(window.history.state as object | null), layer: marker }, '');

    let closedByBack = false;
    const onPop = () => {
      closedByBack = true;
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      const state = window.history.state as { layer?: unknown } | null;
      if (!closedByBack && state?.layer === marker) window.history.back();
    };
  }, [open]);
}
